/**
 * Mobile Bottom Navigation Component
 * 
 * A fixed bottom tab bar for mobile navigation.
 * Shows 5 tabs: Dashboard, Sales, Operations, EOS, More
 * 
 * The "More" tab opens a drawer with additional navigation options.
 */
import React, { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useTheme } from '../contexts/ThemeContext';
import { 
  LayoutDashboard, 
  TrendingUp, 
  Wrench, 
  Target, 
  MoreHorizontal,
  Megaphone,
  Phone,
  Shield,
  MessageSquare,
  Settings,
  LogOut,
  X,
  ChevronRight,
  Sun,
  Moon
} from 'lucide-react';
import { Sheet, SheetContent, SheetHeader, SheetTitle } from './ui/sheet';
import { Button } from './ui/button';
import { Separator } from './ui/separator';
import { toast } from 'sonner';

// Admin roles that can see admin section
const ADMIN_ROLES = ['super_admin', 'admin'];

// Tester role - can see everything to test all flows (but not admin features)
const TESTER_ROLES = ['tester'];

export function MobileBottomNav() {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, logout, isSuperAdmin } = useAuth();
  const { isDark, toggleTheme } = useTheme();
  const [moreDrawerOpen, setMoreDrawerOpen] = useState(false);

  // Check if user has admin access (super_admin, admin)
  const hasAdminAccess = isSuperAdmin || ADMIN_ROLES.includes(user?.role);
  
  // Check if user is a tester (can access everything except admin dashboard)
  const isTester = TESTER_ROLES.includes(user?.role);

  // Main bottom tabs (5 items max)
  const mainTabs = [
    { 
      id: 'dashboard', 
      label: 'Home', 
      icon: LayoutDashboard, 
      path: '/dashboard',
      isActive: (path) => path === '/dashboard' || path === '/admin'
    },
    { 
      id: 'sales', 
      label: 'Sales', 
      icon: TrendingUp, 
      path: '/sales-hub/board',
      isActive: (path) => path.startsWith('/sales-hub') || path.startsWith('/workspace/records')
    },
    { 
      id: 'operations', 
      label: 'Ops', 
      icon: Wrench, 
      path: '/operations-hub/dashboard',
      isActive: (path) => path.startsWith('/operations-hub')
    },
    { 
      id: 'eos', 
      label: 'EOS', 
      icon: Target, 
      path: '/eos/dashboard',
      isActive: (path) => path.startsWith('/eos')
    },
    { 
      id: 'more', 
      label: 'More', 
      icon: MoreHorizontal, 
      action: () => setMoreDrawerOpen(true),
      isActive: () => false
    },
  ];

  // "More" drawer menu items
  const moreMenuItems = [
    { 
      id: 'marketing', 
      label: 'Marketing Hub', 
      icon: Megaphone, 
      path: '/marketing-hub/dashboard',
      show: true
    },
    { 
      id: 'call-center', 
      label: 'Call Center', 
      icon: Phone, 
      path: '/call-center',
      show: true
    },
    { 
      id: 'admin', 
      label: 'Admin Dashboard', 
      icon: Shield, 
      path: '/admin',
      show: hasAdminAccess  // Only super_admin and admin
    },
    { 
      id: 'chat', 
      label: 'Chat', 
      icon: MessageSquare, 
      path: '/chat',
      show: true
    },
    { 
      id: 'settings', 
      label: 'Settings', 
      icon: Settings, 
      path: '/settings',
      show: hasAdminAccess || isTester  // Testers can see settings too
    },
  ];

  const handleTabClick = (tab) => {
    if (tab.action) {
      tab.action();
    } else if (tab.path) {
      navigate(tab.path);
    }
  };

  const handleMoreItemClick = (item) => {
    setMoreDrawerOpen(false);
    navigate(item.path);
  };

  const handleLogout = async () => {
    setMoreDrawerOpen(false);
    await logout();
    toast.success('Logged out successfully');
    navigate('/login');
  };

  return (
    <>
      {/* Bottom Navigation Bar */}
      <nav 
        className="fixed bottom-0 left-0 right-0 z-50 bg-card border-t border-border safe-area-bottom"
        data-testid="mobile-bottom-nav"
      >
        <div className="flex items-center justify-around h-16 px-1">
          {mainTabs.map((tab) => {
            const Icon = tab.icon;
            const isActive = tab.isActive ? tab.isActive(location.pathname) : false;
            
            return (
              <button
                key={tab.id}
                onClick={() => handleTabClick(tab)}
                className={`flex flex-col items-center justify-center flex-1 h-full py-2 px-1 transition-colors ${
                  isActive 
                    ? 'text-primary' 
                    : 'text-muted-foreground hover:text-foreground'
                }`}
                data-testid={`mobile-nav-${tab.id}`}
              >
                <Icon className={`h-5 w-5 ${isActive ? 'text-primary' : ''}`} />
                <span className={`text-[10px] mt-1 font-medium ${isActive ? 'text-primary' : ''}`}>
                  {tab.label}
                </span>
                {isActive && (
                  <div className="absolute bottom-0 left-1/2 -translate-x-1/2 w-8 h-0.5 bg-primary rounded-full" />
                )}
              </button>
            );
          })}
        </div>
      </nav>

      {/* "More" Drawer */}
      <Sheet open={moreDrawerOpen} onOpenChange={setMoreDrawerOpen}>
        <SheetContent side="bottom" className="h-auto max-h-[80vh] rounded-t-2xl">
          <SheetHeader className="pb-4">
            <div className="flex items-center justify-between">
              <SheetTitle>More Options</SheetTitle>
              <Button 
                variant="ghost" 
                size="icon" 
                onClick={() => setMoreDrawerOpen(false)}
                className="h-8 w-8"
              >
                <X className="h-4 w-4" />
              </Button>
            </div>
          </SheetHeader>

          <div className="space-y-1 pb-4">
            {moreMenuItems
              .filter(item => item.show)
              .map((item) => {
                const Icon = item.icon;
                const isActive = location.pathname.startsWith(item.path);
                
                return (
                  <button
                    key={item.id}
                    onClick={() => handleMoreItemClick(item)}
                    className={`w-full flex items-center justify-between px-4 py-3 rounded-lg transition-colors ${
                      isActive 
                        ? 'bg-primary/10 text-primary' 
                        : 'hover:bg-accent'
                    }`}
                    data-testid={`mobile-more-${item.id}`}
                  >
                    <div className="flex items-center gap-3">
                      <Icon className="h-5 w-5" />
                      <span className="font-medium">{item.label}</span>
                    </div>
                    <ChevronRight className="h-4 w-4 text-muted-foreground" />
                  </button>
                );
              })}
          </div>

          <Separator className="my-2" />

          {/* Theme Toggle */}
          <div className="px-4 py-2">
            <button
              onClick={toggleTheme}
              className="w-full flex items-center justify-between px-4 py-3 rounded-lg hover:bg-accent transition-colors"
              data-testid="mobile-theme-toggle"
            >
              <div className="flex items-center gap-3">
                {isDark ? <Sun className="h-5 w-5 text-yellow-500" /> : <Moon className="h-5 w-5 text-slate-600" />}
                <span className="font-medium">{isDark ? 'Light Mode' : 'Dark Mode'}</span>
              </div>
              <div className={`w-12 h-6 rounded-full p-1 transition-colors ${isDark ? 'bg-slate-700' : 'bg-slate-300'}`}>
                <div className={`w-4 h-4 rounded-full bg-white shadow-sm transform transition-transform ${isDark ? 'translate-x-0' : 'translate-x-6'}`} />
              </div>
            </button>
          </div>

          <Separator className="my-2" />

          {/* Logout Button */}
          <div className="pt-2 pb-4">
            <button
              onClick={handleLogout}
              className="w-full flex items-center gap-3 px-4 py-3 rounded-lg text-destructive hover:bg-destructive/10 transition-colors"
              data-testid="mobile-logout-btn"
            >
              <LogOut className="h-5 w-5" />
              <span className="font-medium">Logout</span>
            </button>
          </div>

          {/* User Info */}
          {user && (
            <div className="pt-2 pb-4 border-t border-border">
              <div className="flex items-center gap-3 px-4 py-2">
                <div className="h-10 w-10 rounded-full bg-primary/10 flex items-center justify-center text-primary font-medium">
                  {user.first_name?.[0]}{user.last_name?.[0]}
                </div>
                <div className="flex-1 min-w-0">
                  <p className="font-medium text-sm truncate">
                    {user.first_name} {user.last_name}
                  </p>
                  <p className="text-xs text-muted-foreground truncate">
                    {user.email}
                  </p>
                </div>
              </div>
            </div>
          )}
        </SheetContent>
      </Sheet>
    </>
  );
}

export default MobileBottomNav;
